import isValid from 'date-fns/is_valid'
import parse from 'date-fns/parse'
import { Either, left, right } from 'fp-ts/lib/Either'
import { get, isNil } from 'lodash'

/**
 * オブジェクトを一定の条件でパースし、型定義と一致していることを検証するための関数.
 *
 * 基本アプローチとして "Parse, don't validate" を参照.
 * @see https://sporto.github.io/elm-patterns/basic/parse-dont-validate.html
 * @see https://lexi-lambda.github.io/blog/2019/11/05/parse-don-t-validate/
 */
export type Parser<R extends object, Src = object> = (data: Src) => ParseResult<R>
/**
 * オブジェクトのパース結果.
 */
export type ParseResult<R extends object> = Either<Error[], R>

/**
 * 指定された全ての{@link Parser}が成功した場合のみ成功する新しい{@link Parser}を返す.
 */
export function join<Result extends object>(
  parser: Parser<Result>,
  ...nextParsers: Array<Parser<Result>>
): Parser<Result> {
  return (data: object): ParseResult<Result> => [parser, ...nextParsers].reduce(
    (prev: ParseResult<Result>, next: Parser<Result>): ParseResult<Result> => prev.fold(
      prevE => left(
        next(data).fold(currentE => [...prevE, ...currentE], () => prevE)
      ),
      next
    ),
    right(data as Result)
  )
}
/**
 * 指定されたいずれかの{@link Parser}が成功した場合に成功する新しい{@link Parser}を返す.
 */
export function union<Result extends object>(
  parser: Parser<Result>,
  ...nextParsers: Array<Parser<Result>>
): Parser<Result> {
  return (data: object): ParseResult<Result> => [parser, ...nextParsers].reduce(
    (prev: ParseResult<Result>, next: Parser<Result>): ParseResult<Result> => prev.fold(
      prevE => next(data).fold(
        currentE => left([...prevE, ...currentE]),
        () => right(data as Result)
      ),
      () => prev
    ),
    left([])
  )
}

export function requireKeys<Result extends object>(keys: Array<keyof Result>): Parser<Result> {
  return every(keys, {
    condition: (key, data) => key in data,
    message: key => `${key} is required`,
  })
}

export function notNil<Result extends object>(keys: Array<keyof Result>): Parser<Result> {
  return every(keys, {
    condition: (key, data) => !isNil(get(data, key)),
    message: key => `${key} is null or undefined`,
  })
}

export function be<Result extends object>(keys: Array<keyof Result>, value: Result[keyof Result]): Parser<Result> {
  return every(keys, {
    condition: (key, data) => get(data, key) === value,
    message: key => `${key} is not ${value}`,
  })
}

export function anyOf<
  Result extends object,
  K extends keyof Result
>(key: K, values: Array<Result[K]>): Parser<Result> {
  return (data: object) => {
    const value = get(data, key)

    return values.some(v => v === value)
      ? right(data as Result)
      : left([new Error(`${key} is not any of ${values.join(', ')}`)])
  }
}

export function largerThanOrEqual<Result extends object>(
  keys: Array<keyof Result>,
  value: any
): Parser<Result> {
  return every(keys, {
    condition: (key, data) => get(data, key) >= value,
    message: (key, data) => `value of "${key}"(${get(data, key)}) is not >= ${value}`,
  })
}

export function validDate<Result extends object>(
  keys: Array<keyof Result>
): Parser<Result> {
  return every(keys, {
    condition: (key, data) => isValid(parse(get(data, key))),
    message: (key, data) => `value of "${key}"(${get(data, key)}) is not valid date format`,
  })
}

function every<Result extends object>(
  keys: Array<keyof Result>,
  rule: {
    condition: (key: keyof Result, data: object) => boolean
    message: (key: keyof Result, data: object) => string
  }
): Parser<Result> {
  return (data: object) => {
    const invalid = keys.filter(key => !rule.condition(key, data))

    return invalid.length === 0
      ? right(data as Result)
      : left(invalid.map(key => new Error(rule.message(key, data))))
  }
}
